import React from "react";
import styled from "styled-components";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

/**
 * Generic showcase card component with background image and overlay support
 * Renders child sections using the same pattern as TournamentPageRenderer
 *
 * @param {string} variant - "default" or "gradient-fade" (fades from transparent at top to overlay color)
 * @param {string} backgroundColor - Optional solid background color
 * @param {string} padding - Optional padding override (e.g., "0" for no padding, "1rem" for custom padding)
 * @param {string} marginBottom - Optional margin-bottom override (e.g., "0" for no margin)
 */
const TournamentShowcaseCard = ({ bgImage, overlayColor, backgroundColor, borderRadius = "20px", minHeight, variant = "default", padding, marginBottom, sections = [], renderSection }) => {
  const responsiveBgImage = useResponsiveImageUrl(bgImage || "");

  return (
    <CardWrapper
      bgImage={bgImage ? responsiveBgImage : null}
      overlayColor={overlayColor}
      backgroundColor={backgroundColor}
      borderRadius={borderRadius}
      minHeight={minHeight}
      variant={variant}
      padding={padding}
      marginBottom={marginBottom}
    >
      {sections.map((section, index) => renderSection(section, index))}
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  border-radius: ${props => props.borderRadius};
  margin-bottom: ${props => props.marginBottom !== undefined ? props.marginBottom : '1.25rem'};
  min-height: ${props => props.minHeight || 'auto'};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  padding: ${props => props.padding !== undefined ? props.padding : '2rem'};
  padding-top: ${props => props.padding !== undefined ? props.padding : (props.variant === 'gradient-fade' ? '120px' : '2rem')};
  background-color: ${props => props.backgroundColor || 'transparent'};

  ${(props) =>
    props.bgImage &&
    `
    background-image: url(${props.bgImage});
    background-position: top center;
    background-size: cover;
    background-repeat: no-repeat;
  `}

  ${(props) =>
    props.overlayColor &&
    `
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${
        props.variant === 'gradient-fade'
          ? `linear-gradient(to bottom, transparent 0px, transparent 145px, ${props.overlayColor} 177px)`
          : props.overlayColor
      };
      z-index: 0;
    }
  `}

  > * {
    position: relative;
    z-index: 1;
  }

  h3, h4 {
    font-weight: 700;
  }
  h4 {
    font-size: 1.1rem!important;
  }
  .row {
    margin-bottom: 0 !important;
  }
`;

export default TournamentShowcaseCard;
